import { useMemo } from 'react';

import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { Navigate } from 'react-router';
import { useTitle } from 'react-use';

import DialogImage from '@/components/atoms/Images/DialogImage';
import SubHeaderDialog from '@/components/organisms/SubHeader/SubHeaderDialog';
import SubNavDialog from '@/components/organisms/SubNav/SubNavDialog';
import EmptyPage from '@/components/templates/EmptyPage/EmptyPage';
import PageContentWrapper from '@/components/templates/PageContentWrapper';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import useBrains from '@/hooks/useBrains';
import useDialogs from '@/hooks/useDialogs';
import { cn } from '@/lib/utils';
import { getPermissions } from '@/redux/permissions/selectors';
import { selectAccountSlug, selectBrainId, selectDialogId } from '@/redux/session/selectors';
import { actions } from '@/shared/types/permissions';
import { RootState } from '@/shared/types/state';

import styles from './Dialogs.module.scss';

const greetingStrings = ['greeting', 'greetings', 'hello', 'initial', 'hi'];

export default function Dialogs() {
  const { t } = useTranslation();
  const brainId = useSelector(selectBrainId);
  const { brain } = useBrains(brainId);
  const slug = useSelector(selectAccountSlug);
  const dialogId = useSelector(selectDialogId);
  const { dialogs, listStatus, createDraftDialog } = useDialogs(brainId);

  const canWrite = useSelector((state: RootState) =>
    getPermissions(state, 'brains', actions.WRITE)
  );
  useTitle(t('pages.dialogs', { 0: brain?.name ?? '-' }));

  const greetingDialog = useMemo(
    () => dialogs?.find((dialog) => greetingStrings.includes(dialog.name.toLowerCase())),
    [dialogs]
  );

  // Send the user to the 'greeting' dialog or the first dialog if it exists
  if (listStatus === 'success' && dialogs.length) {
    const path = `/${slug}/ai-agents/${brainId}/workflows/dialogs/${
      dialogId || greetingDialog?.dialog_id || dialogs[0]?.dialog_id
    }`;

    return <Navigate to={path} replace />;
  }

  return (
    <div className={cn(styles.wrapper, 'h-full', { [styles.readOnly]: !canWrite })}>
      <SubNavDialog />
      <SubHeaderDialog />
      <PageContentWrapper>
        {listStatus === 'pending' ? (
          <Skeleton className="h-12 w-full" />
        ) : (
          <EmptyPage>
            <DialogImage />
            <h2>{t('dialog.empty_title')}</h2>
            <Button variant="ghost" disabled={!canWrite} onClick={createDraftDialog}>
              {t('dialog.empty_create_plus')}
            </Button>
          </EmptyPage>
        )}
      </PageContentWrapper>
    </div>
  );
}
